/**
 * Style overrides for react-file-icon (brand colors for Adobe, Office, Apple, etc.).
 * Keys: extension without dot, lowercase.
 */
export type FileIconStyleOverride = Partial<{
  color: string;
  fold: boolean;
  foldColor: string;
  glyphColor: string;
  gradientColor: string;
  gradientOpacity: number;
  labelColor: string;
  labelTextColor: string;
  labelUppercase: boolean;
  radius: number;
  type:
    | '3d'
    | 'acrobat'
    | 'android'
    | 'audio'
    | 'binary'
    | 'code'
    | 'code2'
    | 'compressed'
    | 'document'
    | 'drive'
    | 'font'
    | 'image'
    | 'presentation'
    | 'settings'
    | 'spreadsheet'
    | 'vector'
    | 'video';
}>;

/** Custom styles for common work app extensions (Adobe, Microsoft Office, Apple iWork). */
export const FILE_ICON_CUSTOM_STYLES: Record<string, FileIconStyleOverride> = {
  // Adobe
  psd: {
    color: '#f8f9fb',
    labelColor: '#34364E',
    labelTextColor: '#31C5F0',
    type: 'image',
  },
  ai: {
    color: '#f8f9fb',
    labelColor: '#423325',
    labelTextColor: '#FF7F18',
    type: 'image',
  },
  indd: {
    color: '#f8f9fb',
    labelColor: '#4B2B36',
    labelTextColor: '#FF408C',
    type: 'image',
  },
  // Microsoft Office – Word (+ macro/template OOXML, RTF, ODF)
  doc: {
    color: '#f8f9fb',
    labelColor: '#2C5898',
    type: 'document',
  },
  docx: {
    color: '#f8f9fb',
    labelColor: '#2C5898',
    type: 'document',
  },
  docm: {
    color: '#f8f9fb',
    labelColor: '#2C5898',
    type: 'document',
  },
  dotx: {
    color: '#f8f9fb',
    labelColor: '#2C5898',
    type: 'document',
  },
  dotm: {
    color: '#f8f9fb',
    labelColor: '#2C5898',
    type: 'document',
  },
  rtf: {
    color: '#f8f9fb',
    labelColor: '#2C5898',
    type: 'document',
  },
  odt: {
    color: '#f8f9fb',
    labelColor: '#2C5898',
    type: 'document',
  },
  ott: {
    color: '#f8f9fb',
    labelColor: '#2C5898',
    type: 'document',
  },
  // Microsoft Office – Excel (+ macro/template/binary, CSV/TSV, ODF)
  xls: {
    color: '#f8f9fb',
    labelColor: '#1A754C',
    type: 'spreadsheet',
  },
  xlsx: {
    color: '#f8f9fb',
    labelColor: '#1A754C',
    type: 'spreadsheet',
  },
  xlsm: {
    color: '#f8f9fb',
    labelColor: '#1A754C',
    type: 'spreadsheet',
  },
  xlsb: {
    color: '#f8f9fb',
    labelColor: '#1A754C',
    type: 'spreadsheet',
  },
  xltx: {
    color: '#f8f9fb',
    labelColor: '#1A754C',
    type: 'spreadsheet',
  },
  xltm: {
    color: '#f8f9fb',
    labelColor: '#1A754C',
    type: 'spreadsheet',
  },
  csv: {
    color: '#f8f9fb',
    labelColor: '#1A754C',
    type: 'spreadsheet',
  },
  tsv: {
    color: '#f8f9fb',
    labelColor: '#1A754C',
    type: 'spreadsheet',
  },
  ods: {
    color: '#f8f9fb',
    labelColor: '#1A754C',
    type: 'spreadsheet',
  },
  ots: {
    color: '#f8f9fb',
    labelColor: '#1A754C',
    type: 'spreadsheet',
  },
  // Microsoft Office – PowerPoint (+ macro/template OOXML, ODF)
  ppt: {
    color: '#f8f9fb',
    labelColor: '#D14423',
    type: 'presentation',
  },
  pptx: {
    color: '#f8f9fb',
    labelColor: '#D14423',
    type: 'presentation',
  },
  pptm: {
    color: '#f8f9fb',
    labelColor: '#D14423',
    type: 'presentation',
  },
  potx: {
    color: '#f8f9fb',
    labelColor: '#D14423',
    type: 'presentation',
  },
  potm: {
    color: '#f8f9fb',
    labelColor: '#D14423',
    type: 'presentation',
  },
  odp: {
    color: '#f8f9fb',
    labelColor: '#D14423',
    type: 'presentation',
  },
  otp: {
    color: '#f8f9fb',
    labelColor: '#D14423',
    type: 'presentation',
  },
  // Apple iWork – Pages (word processor)
  pages: {
    color: '#f8f9fb',
    labelColor: '#F1AC48',
    type: 'document',
  },
  // Apple iWork – Numbers (spreadsheet)
  numbers: {
    color: '#f8f9fb',
    labelColor: '#80E662',
    type: 'spreadsheet',
    glyphColor: 'rgba(255,255,255,0.4)',
  },
  // Apple iWork – Keynote (presentations)
  key: {
    color: '#f8f9fb',
    labelColor: '#4084ED',
    type: 'presentation',
    glyphColor: 'rgba(255,255,255,0.4)',
  },
  fgsecret: {
    color: '#f8f9fb',
    labelColor: '#bc8d00', // yellow-dark — matches MainButtonMenu secret icon
    type: 'binary',
    // Package glyph hidden; FileIcon overlays Lucide secret-type mark
    glyphColor: 'transparent',
  },
  fgnote: {
    color: '#f8f9fb',
    labelColor: '#3b82f6', // Tailwind blue-500 — matches MainButtonMenu note icon
    type: 'document',
  },
  tsx: {
    type: 'code',
  },
};

/**
 * Returns brand style overrides for a given extension, if any.
 * Use this so the same extension always gets the same brand look (Adobe, Office, etc.).
 */
export function getBrandStyleForExtension(extension: string): FileIconStyleOverride | undefined {
  const ext = extension?.toLowerCase().trim() || '';
  return ext ? FILE_ICON_CUSTOM_STYLES[ext] : undefined;
}

/**
 * Pastel color palette for file icon labels (react-file-icon).
 * Same extension always maps to the same color, similar to avatarColors by user ID.
 */
export const FILE_ICON_LABEL_COLORS: string[] = [
  '#B8D4E3', // Soft blue
  '#D4B8C7', // Dusty rose
  '#C7D4B8', // Sage
  '#D4C9B8', // Sand
  '#C4B8D4', // Lavender
  '#B8D4C4', // Mint
  '#D4B8A8', // Peach
  '#A8C9D4', // Sky
  '#D4C4B8', // Beige
  '#B8C4D4', // Slate blue
  '#D4B8D4', // Mauve
  '#C9D4B8', // Pale green
  '#B8D4D4', // Aqua
  '#D4B8B8', // Blush
  '#D4D4B8', // Cream
];

/**
 * Simple deterministic hash for a string (extension).
 * Same string always yields the same index.
 */
function hashStringToIndex(str: string): number {
  if (!str || str.length === 0) return 0;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // force 32-bit
  }
  return Math.abs(hash);
}

/**
 * Returns a consistent pastel label color for a file extension.
 * Same extension always returns the same color.
 *
 * @param extension - File extension without dot (e.g. "pdf", "docx")
 * @returns Pastel hex color for react-file-icon labelColor
 */
export function getLabelColorForExtension(extension: string): string {
  const ext = extension?.toLowerCase().trim() || 'file';
  const index = hashStringToIndex(ext) % FILE_ICON_LABEL_COLORS.length;
  return FILE_ICON_LABEL_COLORS[index] ?? FILE_ICON_LABEL_COLORS[0] ?? '#B8D4E3';
}
